import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "@phosphor-icons/react";

/**
 * Cuadro para dibujar la firma con dedo/mouse. Devuelve una data URL PNG
 * al llamar `.export()` desde el ref o via onChange.
 *
 * Props:
 *  - onChange(dataUrl|"")  → callback al terminar cada trazo
 *  - height (default 160)
 *  - label
 *  - "data-testid"
 */
const SignaturePad = forwardRef(function SignaturePad(
  { onChange, height = 160, label = "Firma", "data-testid": testid = "signature-pad" },
  ref
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    const w = parent ? parent.clientWidth : 300;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#FFFFFF";
  };

  useEffect(() => {
    resize();
    const onR = () => resize();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPoint(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (isEmpty) setIsEmpty(false);
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (onChange) onChange(exportPng());
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setIsEmpty(true);
    if (onChange) onChange("");
  };

  const exportPng = () => {
    const canvas = canvasRef.current;
    if (!canvas || isEmpty) return "";
    // Producimos una firma "tinta oscura sobre fondo blanco" independiente del
    // color de trazo del canvas (que es blanco para verse sobre el fondo
    // oscuro del formulario). Estrategia: leer canal alpha del canvas fuente
    // y pintar negro en cada pixel con alpha > 0 sobre un lienzo blanco.
    const off = document.createElement("canvas");
    off.width = canvas.width;
    off.height = canvas.height;
    const octx = off.getContext("2d");
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, off.width, off.height);
    const src = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height);
    const dst = octx.getImageData(0, 0, off.width, off.height);
    for (let i = 0; i < src.data.length; i += 4) {
      const a = src.data[i + 3];
      if (a > 20) {
        // Mezcla proporcional al alpha para conservar antialias en bordes.
        const k = a / 255;
        dst.data[i]     = Math.round(255 * (1 - k));
        dst.data[i + 1] = Math.round(255 * (1 - k));
        dst.data[i + 2] = Math.round(255 * (1 - k));
        dst.data[i + 3] = 255;
      }
    }
    octx.putImageData(dst, 0, 0);
    return off.toDataURL("image/png");
  };

  useImperativeHandle(ref, () => ({ exportPng, clear, isEmpty: () => isEmpty }), [isEmpty]);

  return (
    <div className="w-full" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs uppercase tracking-widest text-gray-500">{label}</label>
        <Button
          type="button"
          onClick={clear}
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-gray-400 hover:text-white"
          data-testid={`${testid}-clear`}
        >
          <Eraser size={14} className="mr-1" /> Borrar
        </Button>
      </div>
      <div className="relative w-full bg-[#0F0F0F] border border-white/10 hover:border-[#FF4D00]/40 transition-colors">
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="touch-none cursor-crosshair block"
          data-testid={`${testid}-canvas`}
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm pointer-events-none select-none">
            Firma aquí con el dedo o mouse
          </div>
        )}
      </div>
    </div>
  );
});

export default SignaturePad;
